import { CHARACTERS, LOCATIONS } from './gameData';
import { v4 } from 'uuid';
import { STORY_CONTENT } from './storyContent';
import { SPRITES } from './assetManifest';
import { getSpriteUrl } from './assetUtils';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

export default class GameLogic {
  constructor() {
    this.position = this.getInitialPosition();
    this.typingSpeed = 100; // letters per second
    this.toTypeText = true;
  }

  getInitialPosition() {
    const firstChapter = Object.keys(STORY_CONTENT.chapters)[0];
    const firstScene = Object.keys(STORY_CONTENT.chapters[firstChapter].scenes)[0];
    const firstBeat = Object.keys(STORY_CONTENT.chapters[firstChapter].scenes[firstScene].beats)[0];

    return {
      chapter: firstChapter,
      scene: firstScene,
      beat: firstBeat,
      eventIndex: 0
    };
  }

  setTypingSpeed(speed) {
    this.typingSpeed = speed;
  }

  getTypingSpeed() {
    return this.typingSpeed;
  }

  setToTypeText(value) {
    this.toTypeText = value;
  }

  getToTypeText() {
    return this.toTypeText;
  }

  getSpriteUrl(spriteId, variation = null) {
    return getSpriteUrl(spriteId, variation);
  }

  reset() {
    this.position = this.getInitialPosition();
    return this.position;
  }

  getCurrentContent() {
    const { chapter, scene, beat, eventIndex } = this.position;

    if (!STORY_CONTENT.chapters[chapter]) {
      throw new Error(`Chapter ${chapter} not found`);
    }

    const currentScene = STORY_CONTENT.chapters[chapter].scenes[scene];
    if (!currentScene) {
      throw new Error(`Scene ${scene} not found in chapter ${chapter}`);
    }

    const currentBeat = currentScene.beats[beat];
    if (!currentBeat) {
      throw new Error(`Beat ${beat} not found in scene ${scene}`);
    }

    if (eventIndex >= currentBeat.events.length) {
      throw new Error(`Event index ${eventIndex} out of bounds`);
    }

    return {
      event: currentBeat.events[eventIndex]
    };
  }

  handleTransition(beat, currentChapter, currentScene, currentBeat) {
    if (!beat.next && !beat.goToChapter && !beat.goToGameOver) {
      console.warn(`Warning: Missing transition property in Chapter: ${currentChapter}, Scene: ${currentScene}, Beat: ${currentBeat}. Attempting to move to next chapter.`);
      
      const chapterKeys = Object.keys(STORY_CONTENT.chapters);
      const currentChapterIndex = chapterKeys.indexOf(currentChapter);
      
      if (currentChapterIndex < chapterKeys.length - 1) {
        const nextChapter = chapterKeys[currentChapterIndex + 1];
        const nextScene = Object.keys(STORY_CONTENT.chapters[nextChapter].scenes)[0];
        const nextBeat = Object.keys(STORY_CONTENT.chapters[nextChapter].scenes[nextScene].beats)[0];
        
        this.position = {
          chapter: nextChapter,
          scene: nextScene,
          beat: nextBeat,
          eventIndex: 0
        };
        return this.position;
      } else {
        return { isGameOver: true };
      }
    }

    if (beat.goToGameOver) {
      return { isGameOver: true };
    } else if (beat.goToChapter) {
      if (!STORY_CONTENT.chapters[beat.goToChapter.chapter]) {
        return { isGameOver: true };
      }
      const nextChapter = STORY_CONTENT.chapters[beat.goToChapter.chapter];
      const nextScene = Object.keys(nextChapter.scenes)[0];
      const nextBeat = Object.keys(nextChapter.scenes[nextScene].beats)[0];
      this.position = {
        chapter: beat.goToChapter.chapter,
        scene: nextScene,
        beat: nextBeat,
        eventIndex: 0
      };
      return this.position;
    } else if (beat.next) {
      const next = beat.next;
      if (!next.scene || !next.beat)
      {
        throw new Error(`Next position must specify scene and beat. Chapter: ${currentChapter}, Scene: ${currentScene}, Beat: ${currentBeat}`);
      }
      this.position = {
        ...this.position,
        scene: next.scene,
        beat: next.beat,
        eventIndex: 0
      };

      return this.position;
    }
  }

  advance() {
    const { chapter, scene, beat, eventIndex } = this.position;
    const currentScene = STORY_CONTENT.chapters[chapter].scenes[scene];
    const currentBeat = currentScene.beats[beat];

    if (eventIndex + 1 < currentBeat.events.length) {
      this.position.eventIndex += 1;
      return this.position;
    }

    return this.handleTransition(currentBeat, chapter, scene, beat);
  }

  makeChoice(choiceIndex) {
    const { chapter, scene, beat, eventIndex } = this.position;
    const { event } = this.getCurrentContent();
    if (event.type !== 'showDialog' || !event.choices) {
      throw new Error('Current event is not a dialog with choices');
    }

    const choice = event.choices[choiceIndex];
    if (!choice) {
      throw new Error(`Invalid choice index: ${choiceIndex}`);
    }

    return this.handleTransition(choice, chapter, scene, beat);
  }
}
